import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BarChart3, Home, FileText, PieChart, Menu, X } from 'lucide-react';
import { createPortal } from 'react-dom';

const Header = () => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const navItems = [
    { path: '/', label: 'Home', icon: Home },
    { path: '/dashboard', label: 'Dashboard', icon: BarChart3 },
    { path: '/policies', label: 'Policy Table', icon: FileText },
    { path: '/aggregate', label: 'Aggregate Data', icon: PieChart },
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Mobile Menu Portal Component
  const MobileMenuPortal = () => {
    if (!mounted || !isMobileMenuOpen) return null;

    return createPortal(
      <>
        {/* Backdrop */}
        <div 
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-[99998]" 
          onClick={closeMobileMenu}
        />
        
        {/* Mobile Menu */}
        <div className="md:hidden fixed top-16 left-0 right-0 bg-white border-b border-quickbooks-border shadow-2xl z-[99999]">
          <nav className="px-4 py-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={closeMobileMenu}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors duration-200 mb-1 ${
                    isActive
                      ? 'bg-enpact-red text-white'
                      : 'text-quickbooks-gray-dark hover:bg-quickbooks-gray hover:text-quickbooks-blue'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium text-base">{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </div>
      </>,
      document.body
    );
  };

  return (
    <>
      <header className="w-full bg-white shadow-quickbooks border-b border-quickbooks-border full-viewport-width relative">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between h-16">
              {/* Logo and Company Info */}
              <div className="flex items-center space-x-2 sm:space-x-4">
                <Link to="/" className="flex items-center space-x-2 sm:space-x-3 hover:opacity-80 transition-opacity duration-200">
                  <img src="/Logo.png" alt="Enpact Logo" className="h-8 w-auto sm:h-10" />
                  <div className="hidden sm:block">
                    <h1 className="text-lg sm:text-xl font-bold text-enpact-red">Enpact</h1>
                    <p className="text-xs sm:text-sm text-quickbooks-gray-dark">Revenue Stream Calculator</p>
                  </div>
                </Link>
              </div>

              {/* Desktop Navigation */}
              <nav className="hidden md:flex items-center space-x-1">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center space-x-2 px-3 sm:px-4 py-2 rounded-lg transition-colors duration-200 ${
                        isActive
                          ? 'bg-enpact-red text-white'
                          : 'text-quickbooks-gray-dark hover:bg-quickbooks-gray hover:text-quickbooks-blue'
                      }`}
                    >
                      <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
                      <span className="font-medium text-sm sm:text-base">{item.label}</span>
                    </Link>
                  );
                })}
              </nav>

              {/* Mobile Menu Button */}
              <button
                onClick={toggleMobileMenu}
                className="md:hidden p-2 rounded-lg text-quickbooks-gray-dark hover:bg-quickbooks-gray hover:text-quickbooks-blue transition-colors duration-200"
                aria-label="Toggle mobile menu"
              >
                {isMobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Portal - Renders outside normal DOM flow */}
      <MobileMenuPortal />
    </>
  );
};

export default Header;
